import { useEffect, useRef } from "react";

export enum DoubleKey {
  Ctrl = "Control",
  Shift = "Shift",
}

interface UseDoubleCtrlOptions {
  enabled: boolean;
  onTrigger: () => void;
  minDelay?: number;
  maxDelay?: number;
  key?: DoubleKey;
}

export const useDoubleHotkey = ({
  enabled,
  onTrigger,
  minDelay = 50,
  maxDelay = 500,
  key = DoubleKey.Ctrl,
}: UseDoubleCtrlOptions) => {
  const lastKeyPressRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    const isValidTriggerKey = (event: KeyboardEvent) => {
      if (key === DoubleKey.Ctrl) {
        return (
          event.key === DoubleKey.Ctrl &&
          !event.altKey &&
          !event.metaKey &&
          !event.shiftKey
        );
      }

      if (key === DoubleKey.Shift) {
        return (
          event.key === DoubleKey.Shift &&
          !event.altKey &&
          !event.metaKey &&
          !event.ctrlKey
        );
      }

      return false;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isValidTriggerKey(event)) {
        return;
      }

      const now = Date.now();
      const timeSinceLastPress = now - lastKeyPressRef.current;

      if (timeSinceLastPress < maxDelay && timeSinceLastPress > minDelay) {
        event.preventDefault();
        onTrigger();
        lastKeyPressRef.current = 0;
        return;
      }

      lastKeyPressRef.current = now;
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [enabled, key, maxDelay, minDelay, onTrigger]);
};
